import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarX } from "lucide-react";
import { Button, EmptyState, cn } from "@ndd/ui";
import type {
  RdHoursResponse,
  RdHoursWeekResponse,
  RdHoursWeekSaveResponse,
} from "../../../../../shared/api-types";
import {
  appointmentViewSearch,
  type AppointmentListSearch,
} from "../../../lib/appointments";
import {
  RD_HOURS_COPY,
  addIsoDays,
  classifyRdHoursError,
  everyWeekSaveBody,
  rdHoursQueryKey,
  templateSource,
  weekSaveBody,
  weekSource,
  weekVisits,
  type DraftSource,
  type RdHoursErrorKind,
  type WeekVisits,
} from "../../../lib/rd-hours-client";
import {
  draftReducer,
  initialDraft,
  isDraftDirty,
  type DraftAction,
  type DraftState,
  type RdHoursMode,
} from "../../../lib/rd-hours-draft";
import {
  useRdHours,
  useRdHoursWeek,
  useSaveRdHours,
  useSaveRdHoursWeek,
} from "../../../lib/use-rd-hours";
import { QueryErrorState } from "../query-error-state";
import { DiscardChangesDialog } from "../discard-changes-dialog";
import { RdHoursEditorView } from "./editor-view";
import {
  inlineNotice,
  readFailureMode,
  ReloadFailedNotice,
  RefetchNotice,
  type ReadFailureMode,
} from "./read-failure";
import { guardedReload, settleSave } from "./settle-save";
import { useUnsavedGuard } from "./use-unsaved-guard";

// The Availability view's editor (NO-878). `RdHoursEditor` reads the RD's
// hours, holds the draft, and saves. Mode and week live in the URL search
// (`mode=week`, `availWeek=`), so the parent owns navigation: it passes the
// current search and a callback that navigates to the next one.

export interface RdHoursEditorProps {
  /** The RD whose hours show; undefined is the viewer's own. */
  rdId: string | undefined;
  /** The page's current search (view=availability, mode, availWeek, rdId). */
  search: AppointmentListSearch;
  /** Navigate to a new search; the editor asks before leaving unsaved edits. */
  onSearchChange: (next: AppointmentListSearch) => void;
}

/** The search for an editor mode and week, keeping the page's RD. */
export function availabilitySearch(
  search: AppointmentListSearch,
  mode: RdHoursMode,
  availWeek?: string
): AppointmentListSearch {
  const base = appointmentViewSearch(search, "availability");

  return mode === "week"
    ? { ...base, mode: "week", ...(availWeek ? { availWeek } : {}) }
    : base;
}

function isRdHoursResponse(value: unknown): value is RdHoursResponse {
  if (typeof value !== "object" || value === null) return false;

  const candidate = value as Record<string, unknown>;

  return (
    typeof candidate.version === "number" &&
    typeof candidate.timeZone === "string" &&
    "template" in candidate
  );
}

function ReadFailure({
  error,
  onRetry,
  onNextWeek,
}: {
  error: unknown;
  onRetry: () => void;
  onNextWeek: () => void;
}) {
  const kind = classifyRdHoursError(error).kind;

  if (kind === "unavailable") {
    return (
      <EmptyState
        icon={CalendarX}
        title={RD_HOURS_COPY.unavailable}
        description="Their hours can be set once their account is ready."
      />
    );
  }

  if (kind === "week_ended") {
    return (
      <EmptyState
        icon={CalendarX}
        title={RD_HOURS_COPY.weekEnded}
        description="Weeks that have ended can't be changed."
        actions={
          <Button type="button" variant="utility" onClick={onNextWeek}>
            Go to the next open week
          </Button>
        }
      />
    );
  }

  return (
    <QueryErrorState
      error={error}
      notFoundText={RD_HOURS_COPY.unavailable}
      onRetry={onRetry}
    />
  );
}

interface Resolved {
  baseKey: string;
  epoch: number;
  source: DraftSource;
  saved: boolean;
}

/** What a save stored, and how to move the live draft's baseline to it. */
interface Baseline {
  source: DraftSource;
  apply: (source: DraftSource) => void;
}

type Resolve = (
  response: RdHoursResponse | undefined,
  saved: boolean,
  baseline?: Baseline
) => Promise<void>;

interface SessionProps {
  mode: RdHoursMode;
  rdId: string | undefined;
  weekStart: string | null;
  nextWeekStart: string;
  thresholdMinutes: number;
  fresh: boolean;
  source: DraftSource;
  saved: boolean;
  /** True from a save's answer until the read-back settles. */
  resolving: boolean;
  bookedVisits: WeekVisits | null;
  canPreviousWeek: boolean;
  /** Rebuild the draft from the server: the save's own answer, or a refetch. */
  resolve: Resolve;
  /** The inline notice over the editor; given the Retry that reloads hours. */
  renderNotice: (onReload: () => void) => React.ReactNode;
  onSearchChange: (next: AppointmentListSearch) => void;
  search: AppointmentListSearch;
}

interface Failure {
  draft: DraftState;
  error: RdHoursErrorKind;
}

/** A conflict stays until the hours are reloaded; any other refusal stands
 * for the draft it answered and clears with the next edit. */
function visibleError(
  failure: Failure | null,
  draft: DraftState
): RdHoursErrorKind | null {
  if (failure === null) return null;

  const stands = failure.error.kind === "version_conflict" || failure.draft === draft;

  return stands ? failure.error : null;
}

/** The PUT answers with the stored week: the draft's baseline moves to it now,
 * so a failed read-back leaves nothing "unsaved" and the next Save uses the
 * new version. */
function weekBaseline(
  data: RdHoursWeekSaveResponse,
  timeZone: string,
  dispatch: (action: DraftAction) => void
): Baseline {
  return {
    source: weekSource({ days: data.days, change: data.change, timeZone }),
    apply: (source) => dispatch({ type: "rebase", source }),
  };
}

function useSessionSave(
  props: SessionProps,
  draft: DraftState,
  dispatch: (action: DraftAction) => void
) {
  const { mode, rdId, weekStart } = props;
  const [failure, setFailure] = React.useState<Failure | null>(null);
  const queryClient = useQueryClient();
  const saveTemplate = useSaveRdHours(rdId);
  const saveWeek = useSaveRdHoursWeek(rdId, weekStart ?? undefined);

  function fail(error: RdHoursErrorKind) {
    // A conflict's body carries the stored hours; they become the draft only
    // when the person asks (Load the latest hours), never silently.
    setFailure({ draft, error });
  }

  function save() {
    if (mode === "week") {
      const body = weekSaveBody({
        version: draft.source.version,
        week: draft.days,
        changed: draft.changed,
      });

      saveWeek.mutate(body, {
        onSuccess: (data) =>
          void props.resolve(
            undefined,
            true,
            weekBaseline(data, draft.timeZone, dispatch)
          ),
        onError: fail,
      });

      return;
    }

    const body = everyWeekSaveBody({
      version: draft.source.version,
      timeZone: draft.timeZone,
      week: draft.days,
    });

    saveTemplate.mutate(body, {
      onSuccess: (data) => void props.resolve(data, true),
      onError: fail,
    });
  }

  function loadLatest() {
    const current =
      failure?.error.kind === "version_conflict" ? failure.error.current : undefined;

    if (isRdHoursResponse(current)) {
      // The conflict body is the stored hours: refresh the read too, so the
      // new-RD copy and the next open week follow it.
      queryClient.setQueryData(rdHoursQueryKey(rdId), current);
      void props.resolve(current, false);

      return;
    }

    void props.resolve(undefined, false);
  }

  return {
    saving: saveTemplate.isPending || saveWeek.isPending,
    error: visibleError(failure, draft),
    save,
    loadLatest,
  };
}

function Session(props: SessionProps) {
  const { mode, weekStart } = props;
  const [draft, dispatch] = React.useReducer(draftReducer, props.source, (source) =>
    initialDraft(mode, source)
  );
  const [showVisits, setShowVisits] = React.useState(true);
  const guard = useUnsavedGuard(isDraftDirty(draft));
  const { saving, error, save, loadLatest } = useSessionSave(props, draft, dispatch);

  function move(next: AppointmentListSearch) {
    guard.guard(() => props.onSearchChange(next));
  }

  const previousWeek = weekStart === null ? null : addIsoDays(weekStart, -7);
  const nextWeek = weekStart === null ? null : addIsoDays(weekStart, 7);

  return (
    <>
      {props.renderNotice(
        guardedReload(guard.guard, () => props.resolve(undefined, true))
      )}
      <RdHoursEditorView
        draft={draft}
        dispatch={dispatch}
        weekStart={weekStart}
        nextWeekStart={props.nextWeekStart}
        thresholdMinutes={props.thresholdMinutes}
        fresh={props.fresh}
        bookedVisits={props.bookedVisits}
        showVisits={showVisits}
        onShowVisits={setShowVisits}
        canPreviousWeek={props.canPreviousWeek}
        onMode={(next) => next !== mode && move(availabilitySearch(props.search, next))}
        onPreviousWeek={() =>
          previousWeek !== null &&
          move(availabilitySearch(props.search, "week", previousWeek))
        }
        onNextWeek={() =>
          nextWeek !== null && move(availabilitySearch(props.search, "week", nextWeek))
        }
        saving={saving}
        resolving={props.resolving}
        error={error}
        saved={props.saved}
        onSave={save}
        onLoadLatest={loadLatest}
      />
      <DiscardChangesDialog
        open={guard.open}
        onOpenChange={(open) => {
          if (!open) guard.cancel();
        }}
        onDiscard={guard.confirm}
        description="Your changes to these hours will be lost."
      />
    </>
  );
}

type TemplateQuery = ReturnType<typeof useRdHours>;
type WeekQuery = ReturnType<typeof useRdHoursWeek>;

/** The hours a save or "Load the latest hours" handed back, per editor key. */
function useResolved(
  baseKey: string,
  mode: RdHoursMode,
  template: TemplateQuery,
  week: WeekQuery
) {
  const [resolved, setResolved] = React.useState<Resolved | null>(null);
  const [resolving, setResolving] = React.useState(false);
  const [reloadFailedKey, setReloadFailedKey] = React.useState<string | null>(null);

  if (resolved !== null && resolved.baseKey !== baseKey) setResolved(null);

  async function readSource(
    response: RdHoursResponse | undefined
  ): Promise<DraftSource | null> {
    if (mode === "week") {
      const result = await week.refetch();

      return result.isSuccess && result.data ? weekSource(result.data) : null;
    }

    if (response) return templateSource(response);

    const result = await template.refetch();

    return result.isSuccess && result.data ? templateSource(result.data) : null;
  }

  const resolve: Resolve = (response, saved, baseline) => {
    const epoch = (resolved?.epoch ?? 0) + 1;

    // A failed read leaves the current draft alone; after a saved PUT it is
    // surfaced so the screen never claims "Saved" over stale hours.
    return settleSave<DraftSource>(
      { saved, baseline: baseline?.source ?? null },
      {
        lock: setResolving,
        advance: (source) => baseline?.apply(source),
        read: () => readSource(response),
        adopt: (source) => setResolved({ baseKey, epoch, source, saved }),
        reloadFailed: (failed) => setReloadFailedKey(failed ? baseKey : null),
      }
    );
  };

  return { resolved, resolve, resolving, reloadFailed: reloadFailedKey === baseKey };
}

interface FailedRead {
  error: unknown;
  retry: () => void;
  mode: ReadFailureMode;
}

function failureOf(query: TemplateQuery | WeekQuery): FailedRead | null {
  const mode = readFailureMode({
    isError: query.isError,
    hasData: query.data !== undefined,
  });

  if (mode === "none") return null;

  return { error: query.error, retry: () => void query.refetch(), mode };
}

/** The failed read to show, if the template or (in week mode) the week failed. */
function failedRead(
  mode: RdHoursMode,
  template: TemplateQuery,
  week: WeekQuery
): FailedRead | null {
  const templateFailure = failureOf(template);

  if (templateFailure !== null) return templateFailure;

  return mode === "week" ? failureOf(week) : null;
}

function readNotice(
  failed: FailedRead | null,
  reloadFailed: boolean,
  onReload: () => void
) {
  const kind = inlineNotice({ reloadFailed, readMode: failed?.mode ?? "none" });

  if (kind === "reload") return <ReloadFailedNotice onRetry={onReload} />;

  return kind === "refetch" && failed !== null ? (
    <RefetchNotice onRetry={failed.retry} />
  ) : null;
}

function stillLoading(
  mode: RdHoursMode,
  templateData: RdHoursResponse | undefined,
  activeData: RdHoursResponse | RdHoursWeekResponse | undefined,
  weekStart: string | undefined,
  weekData: RdHoursWeekResponse | undefined
): boolean {
  if (templateData === undefined || activeData === undefined) return true;

  return mode === "week" && (weekData === undefined || weekStart === undefined);
}

function bookedVisitsFor(
  weekData: RdHoursWeekResponse | undefined,
  weekStart: string | undefined
): WeekVisits | null {
  if (weekData === undefined || weekStart === undefined) return null;

  if (weekData.bookedVisits === null) return null;

  return weekVisits(weekData.bookedVisits, weekStart, weekData.timeZone);
}

function resolvedMeta(resolved: Resolved | null) {
  return { epoch: resolved?.epoch ?? 0, saved: resolved?.saved ?? false };
}

function pickSource(
  resolved: Resolved | null,
  templateData: RdHoursResponse,
  weekData: RdHoursWeekResponse | undefined
): DraftSource {
  if (resolved !== null) return resolved.source;

  return weekData === undefined ? templateSource(templateData) : weekSource(weekData);
}

const SKELETON_DAYS = 7;
const PULSE = "animate-pulse rounded-sm bg-secondary motion-reduce:animate-none";

/** The grid's shape while the hours load: a header row and seven empty days. */
function LoadingHours() {
  return (
    <div role="status" className="flex flex-col gap-3.5">
      <span aria-hidden="true" className={cn(PULSE, "h-8 w-56")} />
      <div
        aria-hidden="true"
        className="grid h-[420px] grid-cols-[56px_repeat(7,minmax(0,1fr))] gap-px overflow-hidden rounded-xl border border-(color:--surface-line,var(--border)) bg-card p-3"
      >
        <span />
        {Array.from({ length: SKELETON_DAYS }, (_, index) => (
          <span key={index} className="flex flex-col gap-2 px-1">
            <span className={cn(PULSE, "h-4 w-10")} />
            <span className={cn(PULSE, "mt-12 h-40")} />
          </span>
        ))}
      </div>
      <span className="text-body text-muted-foreground">Loading hours…</span>
    </div>
  );
}

/**
 * Whose hours, which mode, which week. Rebuilds the draft (a new Session) when
 * the RD, mode or week changes, and when a save or "Load the latest hours"
 * hands back fresh hours; a background refetch never replaces a draft.
 */
export function RdHoursEditor({ rdId, search, onSearchChange }: RdHoursEditorProps) {
  const mode: RdHoursMode = search.mode === "week" ? "week" : "template";
  const template = useRdHours(rdId, true);
  const nextWeekStart = template.data?.nextWeekStart;
  const weekStart = mode === "week" ? (search.availWeek ?? nextWeekStart) : undefined;
  const week = useRdHoursWeek(
    rdId,
    weekStart,
    mode === "week" && nextWeekStart !== undefined
  );
  const baseKey = `${rdId ?? "own"}|${mode}|${weekStart ?? ""}`;
  const { resolved, resolve, resolving, reloadFailed } = useResolved(
    baseKey,
    mode,
    template,
    week
  );
  const failed = failedRead(mode, template, week);

  if (failed !== null && failed.mode === "replace") {
    return (
      <ReadFailure
        error={failed.error}
        onRetry={failed.retry}
        onNextWeek={() => onSearchChange(availabilitySearch(search, "week"))}
      />
    );
  }

  const templateData = template.data;
  const weekData = mode === "week" ? week.data : undefined;
  const activeData = weekData ?? templateData;

  if (stillLoading(mode, templateData, activeData, weekStart, weekData)) {
    return <LoadingHours />;
  }

  if (templateData === undefined) return null;

  const meta = resolvedMeta(resolved);
  const source = pickSource(resolved, templateData, weekData);
  const currentWeek = addIsoDays(templateData.nextWeekStart, -7);

  return (
    <>
      <Session
        key={`${baseKey}|${meta.epoch}`}
        mode={mode}
        rdId={rdId}
        weekStart={weekData === undefined ? null : (weekStart ?? null)}
        nextWeekStart={templateData.nextWeekStart}
        thresholdMinutes={templateData.breakThresholdMinutes}
        fresh={templateData.template === null}
        source={source}
        saved={meta.saved}
        resolving={resolving}
        bookedVisits={bookedVisitsFor(weekData, weekStart)}
        canPreviousWeek={weekStart !== undefined && weekStart > currentWeek}
        resolve={resolve}
        renderNotice={(onReload) => readNotice(failed, reloadFailed, onReload)}
        onSearchChange={onSearchChange}
        search={search}
      />
    </>
  );
}
